import { Helmet } from "react-helmet-async"
import { Trans, useTranslation } from "react-i18next"
import _ from "lodash"

import { commodityUrl } from "../products/products"
import { useProducts } from "../products/ProductsProvider"
import { ProductMappingMatrix } from "../products/ProductMappingMatrix"
import { getTitle, ProtectionIcon } from "../products/MappingState"
import { Link } from "../navigation"
import { useExternal } from "./"
import { CompetitorCommodityLink, CompetitorFamily } from "./external"
import { competitorFamilyUrl } from "./utils"

export default function ProductExternalMapping() {
    const { t } = useTranslation()

    const { commodities } = useProducts()
    const { families, competitorCommodityLinks, postCompetitorCommodityLink, deleteCompetitorCommodityLink } = useExternal()

    const sortedFamilies = _.sortBy(families, 'internalReference')

    if (families.length === 0 || commodities.length === 0) {
        return <>
            {/* @ts-ignore */}
            <Helmet>
                <title>{t('product-mapping')} - {t('external')} | Patent Cockpit</title>
            </Helmet>
            <div className="header-row">
                <h2 className="modern-h2">{t('product-mapping')} - {t('external')}</h2>
            </div>
            <div className="main-content text-center">
                {commodities.length === 0
                    ? <Trans
                        i18nKey="add-products-hint"
                        components={{ portfoliolink: <Link className="text-pcx-500 underline-link" to="/products/portfolio" /> }}
                    />
                    : <p>{t('no-external-families')}</p>}
            </div>
        </>
    }

    return <ProductMappingMatrix<CompetitorFamily, CompetitorCommodityLink>
        pageTitle={`${t('product-mapping')} - ${t('external')} | Patent Cockpit`}
        heading={`${t('product-mapping')} - ${t('external')}`}
        families={sortedFamilies}
        commodities={commodities}
        links={competitorCommodityLinks}
        getFamilyId={f => f.competitorFamilyId}
        renderFamilyHeader={family => <>
            <Link className="underline-link whitespace-nowrap" to={competitorFamilyUrl(family)}>{family.internalReference}</Link>
            <p title={family.familyName} className="text-xs truncate">{family.familyName}</p>
        </>}
        renderCommodityCell={commodity => (
            <Link className="underline-link block pl-3 bg-white dark:bg-pcx-100 whitespace-nowrap" to={commodityUrl(commodity)}>
                {commodity.commodityReference}
            </Link>
        )}
        baseLinkFor={(commodityId, family) => ({
            commodityId,
            competitorFamilyId: family.competitorFamilyId,
            realm: '',
        })}
        isEqualLink={(a, b) => a.commodityId === b.commodityId && a.competitorFamilyId === b.competitorFamilyId}
        postLinks={postCompetitorCommodityLink}
        deleteLinks={deleteCompetitorCommodityLink}
        legend={<ExplanationFooter />}
    />
}

function ExplanationFooter() {
    const { t } = useTranslation()
    return (
        <div className="flex flex-col lg:flex-row gap-4 lg:gap-6 mt-8 sticky sm:-left-4 pl-2">
            {[
                { isUndefined: true, isProtected: true, isThirdParty: false },
                { isUndefined: false, isProtected: true, isThirdParty: false },
                { isUndefined: false, isProtected: false, isThirdParty: false },
                { isUndefined: false, isProtected: true, isThirdParty: true },
                { isUndefined: false, isProtected: false, isThirdParty: true },
            ].map((state, i) => (
                <div key={i} className="inline-flex items-center gap-1 text-sm text-slate-500">
                    <ProtectionIcon {...state} />
                    {t(getTitle(state.isThirdParty, state.isUndefined, state.isProtected))}
                </div>
            ))}
        </div>
    )
}
